import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback
} from 'react';
import { useLocation } from 'react-router';

import useSocket from '@/hooks/useSocket';
import { formatLastUpdate } from '@/utils/formatLastUpdate';

type EntityData = {
  entityName: string;
  entityId: string;
  timezone?: string;
  sentAt?: string;
};

type WebSocketContextType = {
  lastUpdateTime: string;
  readyState: number;
  setEntityData: (entityData: EntityData) => void;
  seedLastUpdate: (sentAt?: string, timezone?: string) => void;
  socketData: any;
};

/**
 * Formats an ISO timestamp for Last Update using the given IANA timezone.
 * @param {string} sentAt - ISO date string from REST or WebSocket meta.
 * @param {string} [timezone] - IANA timezone (entity); falls back to browser.
 * @returns {string} Formatted date for the UI.
 */
const formatSentAtForView = (sentAt: string, timezone?: string): string =>
  formatLastUpdate(sentAt, timezone);

const WebSocketContext = createContext<WebSocketContextType>({
  lastUpdateTime: '',
  readyState: 0,
  setEntityData: () => {},
  seedLastUpdate: () => {},
  socketData: null
});

export const WebSocketProvider: React.FC<{ children: React.ReactNode }> = ({
  children
}) => {
  const initialDateTime = formatLastUpdate(new Date());
  const location = useLocation();
  const previousPathRef = useRef<string>(location.pathname);
  const entityTimezoneRef = useRef<string | undefined>(undefined);
  const hasSocketTimestampRef = useRef(false);
  const [connectWs, setConnectWs] = useState(false);
  const [lastUpdateTime, setLastUpdateTime] = useState<string>(initialDateTime);
  const [socketData, setSocketData] = useState<any>(null);

  const [entityData, setEntityDataState] = useState<EntityData>({
    entityName: '',
    entityId: ''
  });

  /**
   * Formats Last Update from REST sentAt before the first websocket timestamp.
   * @param {string} [sentAt] - ISO date from the installed-product response.
   * @param {string} [timezone] - Turbine IANA timezone.
   */
  const seedLastUpdate = useCallback((sentAt?: string, timezone?: string) => {
    if (hasSocketTimestampRef.current) return;
    if (timezone) entityTimezoneRef.current = timezone;
    if (!sentAt && !timezone) return;
    setLastUpdateTime(
      formatSentAtForView(sentAt || new Date().toISOString(), timezone)
    );
  }, []);

  /**
   * Sets the subscribed entity and keeps its timezone for Last Update formatting.
   * @param {EntityData} nextEntityData - Entity name/id and optional IANA timezone.
   */
  const setEntityData = useCallback(
    (nextEntityData: EntityData) => {
      entityTimezoneRef.current = nextEntityData.timezone;
      setEntityDataState(nextEntityData);
      seedLastUpdate(nextEntityData.sentAt, nextEntityData.timezone);
    },
    [seedLastUpdate]
  );

  const shouldConnect =
    entityData.entityName !== '' && entityData.entityId !== '';

  useEffect(() => {
    setConnectWs(shouldConnect);

    if (!shouldConnect) {
      setSocketData(null);
      hasSocketTimestampRef.current = false;
      setLastUpdateTime(initialDateTime);
      entityTimezoneRef.current = undefined;
    }
  }, [shouldConnect]);

  useEffect(() => {
    const currentPath = location.pathname;
    const previousPath = previousPathRef.current;

    if (previousPath !== currentPath) {
      const isTurbineDetailRoute = /^\/turbines\/[^/]+/.test(currentPath);
      const isLocationDetailRoute = /^\/locations\/[^/]+/.test(currentPath);
      const wasTurbineDetailRoute = /^\/turbines\/[^/]+/.test(previousPath);
      const wasLocationDetailRoute = /^\/locations\/[^/]+/.test(previousPath);

      const isTabChangeOnly =
        (isTurbineDetailRoute && wasTurbineDetailRoute) ||
        (isLocationDetailRoute && wasLocationDetailRoute);

      if (!isTabChangeOnly) {
        setConnectWs(false);
        setSocketData(null);
        hasSocketTimestampRef.current = false;
        setLastUpdateTime(initialDateTime);
        entityTimezoneRef.current = undefined;
      }

      previousPathRef.current = currentPath;
    }
  }, [location.pathname]);

  const onLastWsMessage = (socketResponse: any) => {
    const sentAt = socketResponse?._meta?.sentAt;
    if (sentAt) {
      hasSocketTimestampRef.current = true;
      setLastUpdateTime(formatSentAtForView(sentAt, entityTimezoneRef.current));
    }
    setSocketData(socketResponse?.data);
  };

  const { readyState } = useSocket({
    connectWs,
    entityName: entityData.entityName,
    entityId: entityData.entityId,
    onLastWsMessage
  });

  return (
    <WebSocketContext.Provider
      value={{
        lastUpdateTime,
        readyState,
        setEntityData,
        seedLastUpdate,
        socketData
      }}
    >
      {children}
    </WebSocketContext.Provider>
  );
};

export const useWebSocket = () => useContext(WebSocketContext);
